import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

// Toast类型配置
const toastTypes = {
	success: {
		icon: '✓',
		bgColor: 'bg-green-500',
		textColor: 'text-white',
		borderColor: 'border-green-600'
	},
	error: {
		icon: '✕',
		bgColor: 'bg-red-500',
		textColor: 'text-white',
		borderColor: 'border-red-600'
	},
	warning: {
		icon: '⚠',
		bgColor: 'bg-yellow-500',
		textColor: 'text-white',
		borderColor: 'border-yellow-600'
	},
	info: {
		icon: 'ℹ',
		bgColor: 'bg-blue-500',
		textColor: 'text-white',
		borderColor: 'border-blue-600'
	}
}

// Toast组件
function Toast({ message, type = 'info', duration = 3000, onClose }) {
	const [isVisible, setIsVisible] = useState(true)
	const [isExiting, setIsExiting] = useState(false)

	const toastConfig = toastTypes[type] || toastTypes.info

	useEffect(() => {
		const timer = setTimeout(() => {
			handleClose()
		}, duration)

		return () => clearTimeout(timer)
	}, [duration])

	const handleClose = () => {
		setIsExiting(true)
		setTimeout(() => {
			setIsVisible(false)
			onClose?.()
		}, 300) // 动画持续时间
	}

	if (!isVisible) return null

	return (
		<div
			className={`
				fixed top-4 right-4 z-50 max-w-sm w-full
				transform transition-all duration-300 ease-in-out
				${isExiting ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'}
			`}
		>
			<div
				className={`
					${toastConfig.bgColor} ${toastConfig.textColor}
					border ${toastConfig.borderColor} rounded-lg shadow-lg
					p-4 flex items-center justify-between
				`}
			>
				<div className="flex items-center space-x-3">
					<span className="text-lg font-bold">{toastConfig.icon}</span>
					<span className="text-sm font-medium">{message}</span>
				</div>
				<button
					onClick={handleClose}
					className="ml-4 text-white hover:text-gray-200 transition-colors"
				>
					✕
				</button>
			</div>
		</div>
	)
}

// Toast容器组件
function ToastContainer({ toasts, removeToast }) {
	return createPortal(
		<div className="fixed top-0 right-0 z-50 p-4 space-y-2">
			{toasts.map((toast) => (
				<Toast
					key={toast.id}
					message={toast.message}
					type={toast.type}
					duration={toast.duration}
					onClose={() => removeToast(toast.id)}
				/>
			))}
		</div>,
		document.body
	)
}

// Toast Hook
export function useToast() {
	const [toasts, setToasts] = useState([])

	const addToast = (message, type = 'info', duration = 3000) => {
		const id = Date.now() + Math.random()
		const newToast = { id, message, type, duration }
		
		setToasts(prev => [...prev, newToast])
		
		return id
	}

	const removeToast = (id) => {
		setToasts(prev => prev.filter(toast => toast.id !== id))
	}

	const showSuccess = (message, duration) => addToast(message, 'success', duration)
	const showError = (message, duration) => addToast(message, 'error', duration)
	const showWarning = (message, duration) => addToast(message, 'warning', duration)
	const showInfo = (message, duration) => addToast(message, 'info', duration)

	return {
		toasts,
		addToast,
		removeToast,
		showSuccess,
		showError,
		showWarning,
		showInfo
	}
}

// 导出组件
export { Toast, ToastContainer }
export default ToastContainer 